import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import useRecommendations from '../../api/recommendations';
import { MapPin, Utensils, ArrowDownAz, Filter, ThumbsUp, RollerCoaster, Coffee, Building, Star, Heart, Trash2, User, AlertCircle, ChevronDown, Search, X, Plus, MoreVertical } from 'lucide-react';
import { useSyncedUser } from '../../context/SyncedUserContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";

const RecommendationList = ({recommendations, ratings, onHoverItem, favorites, onRefresh, onToggleFavorite, activeFilter, setActiveFilter, onAddClick}) => {
  const { t } = useTranslation();
  const { syncedUserData } = useSyncedUser();
  
  console.log('RecommendationList props:', { 
    recommendationsCount: recommendations?.length, 
    ratingsKeys: Object.keys(ratings || {}), 
    favoritesCount: favorites?.length,
    ratings: ratings
  });
  
  const user = syncedUserData?.user;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const { showNotification } = useNotification();
  const recommendationsApi = useRecommendations();
  
  const SORT_OPTIONS = [
    { id: 'newest', label: t('recommendations.sort.newest'), icon: ArrowDownAz },
    { id: 'rating', label: t('recommendations.sort.topRated'), icon: Star },
    { id: 'likes', label: t('recommendations.sort.mostLiked'), icon: ThumbsUp },
  ];
  
  const filterCategories = [
    { id: 'all', label: t('recommendations.categories.allPlaces'), icon: Star },
    { id: 'favorites', label: t('recommendations.categories.favorites'), icon: Heart },
    { id: 'restaurant', label: t('recommendations.categories.restaurants'), icon: Utensils },
    { id: 'activity', label: t('recommendations.categories.activities'), icon: RollerCoaster },
    { id: 'cafe', label: t('recommendations.categories.cafes'), icon: Coffee },
    { id: 'culture', label: t('recommendations.categories.culture'), icon: Building },
    { id: 'mine', label: t('recommendations.categories.myRecommendations'), icon: User },
  ];
  
  const getCategoryColor = (type) => {
    const colorMap = {
      'Restaurant': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'Activity': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'Cafe': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'Culture': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return colorMap[type] || 'bg-neutral-100 text-neutral-700';
  };
  
  // Load sortBy from localStorage or default to 'newest'
  const [sortBy, setSortBy] = useState(() => {
    const savedSort = localStorage.getItem('recommendationSortBy');
    return savedSort || 'newest';
  });
  
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  
  useEffect(() => {
    localStorage.setItem('recommendationSortBy', sortBy);
  }, [sortBy]);
  
  const handleDelete = async (recommendation) => {
    setSelectedForDelete(recommendation);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      console.log('Deleting recommendation:', selectedForDelete.id, 'with userId:', syncedUserData?.user?.id);
      await recommendationsApi.deleteRecommendation(selectedForDelete.id, syncedUserData?.user?.id);
      showNotification(t('recommendations.notifications.deleted'), 'success');
      showNotification(t('recommendations.notifications.pointsDeducted'), 'warning');
      onRefresh(); // Call refresh after successful deletion
      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Delete error:', error);
      showNotification(t('recommendations.notifications.deleteFailed'), 'error');
    }
  };


  // Handle click on recommendation
  const handleSelectItem = (rec) => {
    setSelectedItem(rec);
    if (onHoverItem) {
      onHoverItem(rec);
    }
  };

  const filteredRecommendations = recommendations.filter(rec => {
    // Filter by category
    const isLiked = favorites.includes(rec.id) || ratings[rec.id]?.isLiked;
    const matchesCategory = activeFilter === 'all' ? true :
                           activeFilter === 'favorites' ? isLiked :
                           activeFilter === 'mine' ? rec.user_id === user?.id :
                           rec.type.toLowerCase() === activeFilter;
    
    // Filter by search
    const matchesSearch = !searchQuery ? true :
                         (rec.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                         (rec.description?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                         (rec.address?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const sortedRecommendations = [...filteredRecommendations].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (ratings[b.id]?.rating || 0) - (ratings[a.id]?.rating || 0);
      case 'likes':
        return (ratings[b.id]?.likesCount || 0) - (ratings[a.id]?.likesCount || 0);
      default:
        // Fix the date sorting - using created_at instead of date
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Get active filter icon and label
  const getActiveFilterInfo = () => {
    const filter = filterCategories.find(c => c.id === activeFilter) || filterCategories[0];
    return filter;
  };

  // Count active filters for badge
  const activeFilterCount = [
    activeFilter !== 'all',
    sortBy !== 'newest', // Add this line to count sort as a filter
    searchQuery.trim() !== ''
  ].filter(Boolean).length;
  
  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter); // This will trigger DiscoverPage's handleFilterChange
  };
  
  const resetFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
    // Note: not resetting sortBy here, so it will be preserved
  };
  
  const resetAllFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
    setSortBy('newest');
    localStorage.removeItem('recommendationSortBy');
  };
  
  // Card component to avoid rerendering issues
  const RecommendationCard = ({ rec, onSelectItem, isSelected, favorites, onToggleFavorite, handleDelete, user, ratings }) => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };
    
    const isLiked = favorites.includes(rec.id) || ratings[rec.id]?.isLiked;
    console.log(`Card ${rec.id} - isLiked:`, isLiked, 'favorites:', favorites.includes(rec.id), 'ratings:', ratings[rec.id]?.isLiked);

    return (
      <Card 
        key={rec.id} 
        className={`p-3 md:p-4 hover:shadow-lg transition-all cursor-pointer ${
          isSelected ? 'shadow-md ring-1 ring-primary/20' : ''
        }`}
        onClick={() => onSelectItem(rec)}
      >
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1.5 md:gap-2 flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <h3 className="text-base md:text-lg font-semibold line-clamp-1">{rec.title}</h3>
              <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-md whitespace-nowrap ml-2 uppercase font-medium tracking-wider ${getCategoryColor(rec.type)}`}>
                {rec.type}
              </span>
            </div>
            
            {/* Enhanced author info with date */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>{t('recommendations.card.addedBy', { author: rec.author })}</span>
              <span>•</span>
              <span>{formatDate(rec.created_at)}</span>
            </div>
            
            <StarRating 
              rating={ratings[rec.id]?.rating || 0} 
              likesCount={ratings[rec.id]?.likesCount || 0} 
            />
            <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-3">{rec.description}</p>
            <div className="flex items-center text-xs md:text-sm text-muted-foreground">
              <MapPin className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">{rec.address}</span>
            </div>

            {/* Mobile action buttons at bottom */}
            <div className="flex items-center justify-end gap-2 mt-1 md:hidden">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleFavorite(rec.id);
                }}
                className="p-1.5 hover:bg-neutral-100 rounded-full"
                aria-label={isLiked ? t('recommendations.actions.unlike') : t('recommendations.actions.like')}
              >
                <Heart className={`w-4 h-4 ${
                  isLiked
                  ? 'fill-red-500 text-red-500'
                  : 'text-neutral-400'
                }`} />
              </button>
              {rec.user_id === user?.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded-full hover:bg-neutral-100"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={t('recommendations.actions.options')}
                    >
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      className="text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(rec);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('recommendations.actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Desktop action buttons on right side with dropdown */}
          <div className="hidden md:flex md:flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleFavorite(rec.id);
                }}
                className="p-2 hover:bg-neutral-100 rounded-full"
                aria-label={isLiked ? t('recommendations.actions.unlike') : t('recommendations.actions.like')}
              >
                <Heart className={`w-5 h-5 ${
                  isLiked
                  ? 'fill-red-500 text-red-500'
                  : 'text-neutral-400'
                }`} />
              </button>
              {rec.user_id === user?.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-2 rounded-full hover:bg-neutral-100"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={t('recommendations.actions.options')}
                    >
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      className="text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(rec);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('recommendations.actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };
  
  const StarRating = ({ rating, likesCount }) => {
    console.log('StarRating props:', { rating, likesCount });
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star 
              key={star} 
              className={`w-3 h-3 md:w-4 md:h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300'}`} 
            />
          ))}
        </div>
        <span className="text-xs md:text-sm text-muted-foreground">
          {t('recommendations.card.likes', { count: likesCount })}
        </span>
      </div>
    );
  };
  
  const DeleteConfirmModal = ({ isOpen, onClose, onConfirm }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-neutral-800 p-4 md:p-6 rounded-lg max-w-md mx-4">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="text-red-500 w-5 h-5 md:w-6 md:h-6" />
            <h3 className="text-base md:text-lg font-semibold">{t('recommendations.deleteModal.title')}</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4 md:mb-6">
            {t('recommendations.deleteModal.description')}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-3 py-1.5 md:px-4 md:py-2 rounded-md text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={onConfirm}
              className="px-3 py-1.5 md:px-4 md:py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              {t('recommendations.deleteModal.confirmButton')}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  const FilterDrawer = ({ 
    isOpen, 
    onClose, 
    sortBy, 
    setSortBy, 
    activeFilter, 
    setActiveFilter, 
    searchQuery, 
    setSearchQuery,
    resetFilters 
  }) => {
    // Local state to store values while drawer is open
    const [localSortBy, setLocalSortBy] = useState(sortBy);
    const [localActiveFilter, setLocalActiveFilter] = useState(activeFilter);
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
    
    // Reset local values when drawer opens
    useEffect(() => {
      if (isOpen) {
        setLocalSortBy(sortBy);
        setLocalActiveFilter(activeFilter);
        setLocalSearchQuery(searchQuery);
      }
    }, [isOpen, sortBy, activeFilter, searchQuery]);
    
    // Handle apply - update parent state with local values
    const handleApply = () => {
      // Apply all changes at once
      localStorage.setItem('recommendationSortBy', localSortBy);
      setSortBy(localSortBy);
      setActiveFilter(localActiveFilter);
      setSearchQuery(localSearchQuery);
      onClose();
    };
    
    // Modified to preserve the sort selection
    const handleReset = () => {
      setLocalActiveFilter('all');
      setLocalSearchQuery('');
      // Don't reset sort automatically
    };

    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[85vh]">
          <div className="flex flex-col h-[85vh]">
            <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-2">
              <div className="flex items-center justify-between">
                <DrawerTitle className="flex items-center gap-2 text-base">
                  <Filter className="h-5 w-5 text-primary" />
                  <span>{t('recommendations.filters.title')}</span>
                </DrawerTitle>
                <DrawerClose className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  <X className="h-4 w-4" />
                </DrawerClose>
              </div>
              <DrawerDescription className="mt-1">
                {t('recommendations.filters.description')}
              </DrawerDescription>
            </DrawerHeader>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
              {/* Sort Options */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium mb-2">{t('recommendations.filters.sortBy')}</h4>
                <div className="grid grid-cols-3 gap-2">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setLocalSortBy(option.id)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
                        localSortBy === option.id
                          ? 'bg-primary text-white'
                          : 'bg-neutral-100 dark:bg-neutral-800'
                      }`}
                    >
                      <option.icon className="w-4 h-4" />
                      <span className="text-sm">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Category Options */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium mb-2">{t('recommendations.filters.category')}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {filterCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => setLocalActiveFilter(category.id)}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${
                          localActiveFilter === category.id
                            ? 'bg-primary text-white'
                            : 'bg-neutral-100 dark:bg-neutral-800'
                        }`}
                      >
                        <CategoryIcon className="w-4 h-4" />
                        <span className="text-sm">{category.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Search Input */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium mb-2">{t('recommendations.filters.search')}</h4>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <input
                    type="text"
                    placeholder={t('recommendations.filters.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 
                               border border-transparent focus:border-primary focus:ring-2 
                               focus:ring-primary/20 transition-all duration-200"
                    value={localSearchQuery}
                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Reset Filters (only if filters are applied) */}
              {(localActiveFilter !== 'all' || localSearchQuery) && (
                <div className="pt-2">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleReset}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('recommendations.filters.resetFilters')}
                  </Button>
                </div>
              )}
              
              {/* Add Reset All button to reset everything including sort */}
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setLocalSortBy('newest');
                    setLocalActiveFilter('all');
                    setLocalSearchQuery('');
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('recommendations.filters.resetAllFilters')}
                </Button>
              </div>
            </div>

            {/* Footer with Apply button */}
            <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-5">
              <Button
                onClick={handleApply}
                className="w-full"
              >
                {t('recommendations.filters.applyFilters')}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="w-full mt-2">
                  {t('common.cancel')}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    );
  };

  return (
    <>
    <div className="h-screen p-4 md:p-6 space-y-3 md:space-y-6 overflow-y-auto">
      {/* Header with title, filter button and add button */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold">{t('recommendations.title')}</h1>
        <div className="flex items-center gap-2">
          {/* Mobile filter button */}
          <Button 
            variant="outline" 
            size="icon" 
            className="md:hidden relative"
            onClick={() => setShowMobileFilters(true)}
            aria-label={t('recommendations.actions.showFilters')}
          >
            <Filter className="h-5 w-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
          
          {/* Add New Place button */}
          <Button 
            onClick={onAddClick}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('recommendations.actions.newPlace')}</span>
          </Button>
        </div>
      </div>
      
      {/* Desktop Filter Bar - Improved Layout */}
      <div className="hidden md:block">
        <Card className="p-4 bg-white dark:bg-neutral-900">
          <div className="flex flex-col gap-4">
            {/* First row with sort options and category dropdown */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Sort Options */}
              <div className="flex gap-2">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSortBy(option.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${
                      sortBy === option.id
                        ? 'bg-primary text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800'
                    }`}
                  >
                    <option.icon className="w-4 h-4" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              
              {/* Category Select Dropdown */}
              <div className="w-56">
                <Select 
                  defaultValue="all"
                  value={activeFilter} 
                  onValueChange={handleFilterChange}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue>
                      {(() => {
                        const filter = getActiveFilterInfo();
                        const FilterIcon = filter.icon;
                        return (
                          <div className="flex items-center gap-2">
                            <FilterIcon className="w-4 h-4" />
                            <span>{filter.label}</span>
                          </div>
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>{t('recommendations.filters.categories')}</SelectLabel>
                      {filterCategories.map(category => {
                        const CategoryIcon = category.icon;
                        return (
                          <SelectItem key={category.id} value={category.id}>
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="w-4 h-4" />
                              <span>{category.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Second row with full-width search */}
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  type="text"
                  placeholder={t('recommendations.filters.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 h-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Reset Filters Button (only shows when filters are applied) */}
              {(activeFilter !== 'all' || searchQuery) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetFilters}
                  className="gap-1.5 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                  {t('common.clear')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
      
      {/* Mobile Filters Drawer for smooth bottom sheet experience */}
      <FilterDrawer 
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        sortBy={sortBy}
        setSortBy={setSortBy}
        activeFilter={activeFilter}
        setActiveFilter={handleFilterChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        resetFilters={resetAllFilters}  // Use the complete reset for mobile drawer
      />
      
      <div className="space-y-3 md:space-y-4">
        {sortedRecommendations.length > 0 ? sortedRecommendations.map((rec) => (
          <RecommendationCard
            key={rec.id}
            rec={rec}
            onSelectItem={handleSelectItem}
            isSelected={selectedItem?.id === rec.id}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
            handleDelete={handleDelete}
            user={user}
            ratings={ratings}
          />
        )) : (
          <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <MapPin className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium">{t('recommendations.emptyState.title')}</h3>
            <p className="text-muted-foreground mt-2 text-sm px-4 text-center">
              {searchQuery || activeFilter !== 'all' 
                ? t('recommendations.emptyState.withFilters') 
                : t('recommendations.emptyState.noRecommendations')
              }
            </p>
            {(searchQuery || activeFilter !== 'all') && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={resetFilters}
              >
                <X className="w-4 h-4 mr-2" />
                {t('recommendations.filters.clearFilters')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
    <DeleteConfirmModal 
      isOpen={deleteModalOpen} 
      onClose={() => setDeleteModalOpen(false)} 
      onConfirm={confirmDelete} 
    />
    </>
  );
};

export default RecommendationList;